import moment from 'moment-timezone';

const INVENTORY_POLICY_CONTINUE = 'continue';
const DISABLED_PRE_ORDER_STATUS_SHOW_MESSAGE = 'showMessage';

export default class PreOrder {
  static PREORDER_STATUS_ENABLED = 1;
  static PREORDER_STATUS_DISABLED = 0;
  static PREORDER_STATUS_SOLD_OUT = -1;

  /**
   * @type {AppConfig}
   */
  #config;

  /**
   * @type {{country_code: string}|null}
   */
  #geoData;

  /**
   * @type {{}}
   */
  #cartApi;

  /**
   * @param {AppConfig} config
   * @param {{country_code: string}|null} geoData
   */
  constructor(config, geoData) {
    this.#config = config;
    this.#geoData = geoData;
    this.#cartApi = Spurit.global.cartPool.getAdapter(config.env().appName);
  }

  /**
   * @param {{inventory_management:string, inventory_policy:string, inventory_quantity:number}} variant
   * @param {{date_start, date_stop, config, is_active: boolean}} settings
   * @return {number}
   */
  preorderStatusForVariant(variant, settings) {
    if (!this.constructor.isVariantAllowed(variant, settings)) {
      if (
        !this.constructor.isVariantAllowed(variant, settings, true) &&
        this.#config.settings().config.disabledPreOrderStatus === DISABLED_PRE_ORDER_STATUS_SHOW_MESSAGE &&
        settings.is_active
      ) {
        return this.constructor.PREORDER_STATUS_SOLD_OUT;
      } else {
        return this.constructor.PREORDER_STATUS_DISABLED;
      }
    }
    if (
      !this.constructor.checkLimits(variant, settings, this.#config.settings().timezone) ||
      !this.constructor.checkLocation(this.#config.settings().config, this.#geoData)
    ) {
      return this.constructor.PREORDER_STATUS_SOLD_OUT;
    }

    let quantityTotal = this.#getVariantQuantityFromCart(variant.id);
    let inStockQuantity = variant.inventory_quantity;
    let preOrderLimit = settings.config.quantityLimit;
    if (preOrderLimit && quantityTotal >= preOrderLimit + inStockQuantity) {
      return this.constructor.PREORDER_STATUS_SOLD_OUT;
    }

    return this.constructor.PREORDER_STATUS_ENABLED;
  }

  /**
   * @param {{inventory_management:string, inventory_policy:string, inventory_quantity:number}} variant
   * @param {{is_active: boolean}} settings
   * @param {boolean} ignoreInventoryQuantity
   * @returns {boolean}
   */
  static isVariantAllowed(variant, settings, ignoreInventoryQuantity = false) {
    if (!settings || !settings.is_active) {
      return false;
    }
    return !!(
      variant.inventory_policy === INVENTORY_POLICY_CONTINUE &&
      (ignoreInventoryQuantity || variant.inventory_quantity <= 0)
    );
  }

  /**
   * @param {{inventory_quantity:number}} variant
   * @param {{date_start, date_stop, config}} settings
   * @param {string} timezone
   * @returns {boolean}
   */
  static checkLimits(variant, settings, timezone) {
    if (!settings) {
      return true;
    }
    if (!timezone) {
      throw new Error('Merchant timezone is not found');
    }

    const now = moment().tz(timezone).startOf('day');
    const dateStart = moment.tz(settings.date_start, timezone);
    const dateStop = moment.tz(settings.date_stop, timezone);

    if (settings.date_start && dateStart > now) {
      return false;
    }
    if (settings.date_stop && dateStop < now) {
      return false;
    }
    return !(
      settings.config
      && settings.config.quantityLimit
      && settings.config.quantityLimit <= -1 * variant.inventory_quantity
    );
  }

  /**
   * @param {{locations: []}} shopConfig
   * @param {{country_code: string}|null} currentGeoData
   * @returns {boolean}
   */
  static checkLocation(shopConfig, currentGeoData) {
    if (!currentGeoData) {
      return true;
    }
    return !!(currentGeoData.country_code && Object.values(shopConfig.locations).find(l => l === currentGeoData.country_code));
  }

  /**
   * @param {number} variantId
   * @returns {number}
   */
  #getVariantQuantityFromCart(variantId) {
    let quantityTotal = 0;
    this.#cartApi.getItems()
      .filter(cartItem => cartItem.variant_id === variantId)
      .forEach(cartItem => quantityTotal += cartItem.quantity);
    return quantityTotal;
  }
}